/**
 * A visão "Certificados e carimbo" (E157.F3, GUS-157, ADR-0107 §2.7). Espelha
 * `apps/api/types/certificados_e_carimbo.py`.
 *
 * - `GET /casos/:id/exame-eletronico/certificados`: por peça, a tabela Ponto / Leitura ou
 *   situação / Fonte composta no servidor sobre o resultado persistido do exame — a assinatura
 *   embutida, a cadeia de confiança, a revogação na data pertinente, o carimbo do tempo, o
 *   certificado de atributo (quando há manifesto), os códigos lidos e a aparência —, cada linha
 *   com os detalhes técnicos por expansão e o limite no rodapé.
 *
 * É da equipe: o banco recebe 403. A tela não recalcula nada: mostra o que o servidor compôs.
 */
import axios, { type AxiosInstance } from 'axios';

import type { VereditoAssinaturaDigital } from '@/lib/exame_eletronico_assinatura_digital';

export type PontoDaConferencia =
  | 'assinatura_embutida'
  | 'cadeia_de_confianca'
  | 'revogacao_na_data_pertinente'
  | 'carimbo_do_tempo'
  | 'certificado_de_atributo'
  | 'codigos_lidos'
  | 'aparencia';

export type SituacaoDoPonto =
  | 'confere'
  | 'nao_confere'
  | 'ausente'
  | 'nao_conferivel'
  | 'nao_executado';

export interface DetalheTecnicoData {
  rotulo: string;
  valor: string;
}

export interface LinhaDaConferenciaData {
  ponto: PontoDaConferencia;
  campo: string | null;
  situacao: SituacaoDoPonto;
  leitura: string;
  fonte: string;
  detalhes: DetalheTecnicoData[];
}

export interface PecaConferidaData {
  documento_id: string;
  letra: string | null;
  nome: string;
  verificada: boolean;
  regua: string | null;
  veredito: VereditoAssinaturaDigital | null;
  linhas: LinhaDaConferenciaData[];
}

/** E183.F2 (GUS-157, ADR-0134): o estado de uma verificação do VCT, de V1 a V12. */
export type EstadoVct = 'confere' | 'nao_confere' | 'nao_verificado' | 'nao_se_aplica' | 'alerta';

/** O consolidado de um carimbo: o sem par pelo resumo é `nao_vinculado`; senão, o pior estado. */
export type ConsolidadoDoCarimbo =
  | 'confere'
  | 'nao_confere'
  | 'alerta'
  | 'nao_verificado'
  | 'nao_vinculado';

export type SituacaoDoVinculo = 'executado' | 'nao_se_aplica' | 'nao_executado';

export interface VerificacaoDoCarimboData {
  id: string;
  nome: string;
  estado: EstadoVct;
  motivo: string | null;
  detalhes: DetalheTecnicoData[];
}

export interface CarimboDoCasoData {
  carimbo: string;
  carimbo_sha256: string;
  coberto: string | null;
  testados: string[];
  consolidado: ConsolidadoDoCarimbo;
  instante: string | null;
  verificacoes: VerificacaoDoCarimboData[];
}

export interface VinculoDoCarimboData {
  situacao: SituacaoDoVinculo;
  motivo: string | null;
  carimbos: CarimboDoCasoData[];
}

export interface CertificadosECarimboData {
  case_id: string;
  pecas: PecaConferidaData[];
  limite: string;
  vinculo?: VinculoDoCarimboData | null;
}

/** O limite no rodapé, como o servidor o entrega (`LIMITE_DA_VISAO`); a tela mostra o do payload. */
export const LIMITE_DA_VISAO =
  'Selo gráfico e frase do fornecedor não são assinatura digital. Consulta em linha e portal não executados.';

export type MotivoDaFalhaDaVisao = 'sem_permissao' | 'nao_encontrado' | 'indisponivel';

export class CertificadosECarimboError extends Error {
  constructor(readonly motivo: MotivoDaFalhaDaVisao) {
    super(motivo);
    this.name = 'CertificadosECarimboError';
  }
}

const HTTP_FORBIDDEN = 403;
const HTTP_NOT_FOUND = 404;

function motivoDoErro(err: unknown): MotivoDaFalhaDaVisao {
  if (!axios.isAxiosError(err)) return 'indisponivel';
  if (err.response?.status === HTTP_FORBIDDEN) return 'sem_permissao';
  if (err.response?.status === HTTP_NOT_FOUND) return 'nao_encontrado';
  return 'indisponivel';
}

export async function lerCertificadosECarimbo(
  client: AxiosInstance,
  caseId: string,
): Promise<CertificadosECarimboData> {
  try {
    const { data } = await client.get<CertificadosECarimboData>(
      `/casos/${caseId}/exame-eletronico/certificados`,
    );
    return data;
  } catch (err) {
    throw new CertificadosECarimboError(motivoDoErro(err));
  }
}
